import type { NavGroup } from "../../nav/navConfig.js";

/**
 * Does this nav group still have anything underneath it once gating has run?
 *
 * A group header is a promise: expanding it shows something. When every child
 * has been filtered away — by role, feature flag, permission, installed app or
 * an async capability gate — the header is a dead end, so callers drop it
 * rather than render a chevron that opens onto nothing.
 *
 * Two things this deliberately does NOT look at:
 *
 * - Expanded/collapsed state. A collapsed group is full, not empty; its panel
 *   is merely closed. Emptiness is a property of the group's children, never
 *   of how the reader last left it.
 * - Which children a particular surface happens to paint. The sidebar renders
 *   `disabled` entries as "Coming soon" while the mobile drawer skips them;
 *   keeping this purely structural means both surfaces agree on whether a group
 *   exists, and no entry that one surface can show is hidden on the strength of
 *   another surface's rendering choice.
 *
 * Recursive, so an inner group that empties out can in turn empty its parent.
 */
export const hasRenderableNavChildren = (group: NavGroup): boolean => {
  if (group.items.length > 0) return true;
  return (group.groups ?? []).some(hasRenderableNavChildren);
};
